// Copyright (c) 2026 Michael F. Kamprath
//
// telemetry.js — anonymous usage-stats client for I Hear Ewe.
//
// Collects four event types (page_load, pane_swipe, pane_first_view,
// item_tap) and POSTs them in batches to the telemetry server. The
// module is entirely opt-in: if the user has turned off the "Share
// anonymous usage stats" toggle in the settings drawer, no events
// are queued and no network requests are made.
//
// Design principles:
//   - Anonymous: no user ids, no device fingerprints, no cookies.
//   - Offline-tolerant: if the network is down, events are silently
//     dropped — no localStorage queue, no retry-on-reconnect.
//   - At-most-once-ish: a batch that fails after exhausting retries
//     is gone forever. Theoretical at-least-once duplicates on a
//     5xx-after-commit window are accepted (microseconds).
//   - Server-configured: batch size, flush interval, and retry
//     delays are fetched from GET /api/config on cold start, with
//     sensible hard-coded fallbacks if the fetch fails.

import { getShareStats } from './settings.js';
import { getLocale } from './i18n.js';

// --- Hard-coded fallbacks (used when /api/config is unreachable) ----------

const DEFAULT_BATCH_SIZE = 20;
const DEFAULT_FLUSH_INTERVAL_MS = 2000;
const DEFAULT_RETRY_DELAYS_MS = [1000, 10000, 100000];

// --- Module state ---------------------------------------------------------

/** @type {{ batch_size: number, flush_interval_ms: number, retry_delays_ms: number[] }} */
let config = {
  batch_size: DEFAULT_BATCH_SIZE,
  flush_interval_ms: DEFAULT_FLUSH_INTERVAL_MS,
  retry_delays_ms: [...DEFAULT_RETRY_DELAYS_MS],
};

/** @type {Array<{ type: string, id?: string }>} */
let queue = [];

/** @type {ReturnType<typeof setTimeout> | null} */
let flushTimer = null;

/** Whether the module has been destroyed (kill switch). */
let destroyed = false;

/** Whether config has been fetched successfully at least once. */
let configFetched = false;

// --- Config fetch ---------------------------------------------------------

/**
 * Fetch batching/retry config from the server. On failure, the
 * hard-coded defaults remain in effect. This is fire-and-forget —
 * the caller doesn't need to await it before queuing events.
 *
 * @param {string} [configUrl='/api/config']
 * @returns {Promise<void>}
 */
export async function fetchConfig(configUrl = '/api/config') {
  if (configFetched || destroyed) return;
  try {
    const resp = await fetch(configUrl);
    if (!resp.ok) return;
    const json = await resp.json();
    if (
      typeof json.batch_size === 'number' &&
      typeof json.flush_interval_ms === 'number' &&
      Array.isArray(json.retry_delays_ms)
    ) {
      config = {
        batch_size: json.batch_size,
        flush_interval_ms: json.flush_interval_ms,
        retry_delays_ms: json.retry_delays_ms,
      };
      configFetched = true;
      // Restart the flush timer with the new interval if one is running.
      if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = setTimeout(flush, config.flush_interval_ms);
      }
    }
  } catch {
    // Network error — stick with defaults.
  }
}

// --- Event queueing -------------------------------------------------------

/**
 * Queue a telemetry event. If the user has opted out, the call is a
 * no-op. When the queue reaches batch_size, a flush is triggered
 * immediately. Otherwise the periodic flush timer will send whatever
 * has accumulated.
 *
 * @param {string} type - One of 'page_load', 'pane_swipe', 'pane_first_view', 'item_tap'.
 * @param {string} [id] - Pane or item id (required for all types except page_load).
 */
export function track(type, id) {
  if (destroyed) return;
  if (!getShareStats()) return;

  const event = { type, locale: getLocale() };
  if (id !== undefined) {
    event.id = id;
  }
  queue.push(event);

  // Start the flush timer on the first queued event.
  if (flushTimer === null) {
    flushTimer = setTimeout(flush, config.flush_interval_ms);
  }

  // Flush immediately when the batch is full.
  if (queue.length >= config.batch_size) {
    flush();
  }
}

// --- Flushing -------------------------------------------------------------

/**
 * Flush the current queue to the server. Clears the queue
 * synchronously before the async POST so new events that arrive
 * during the request go into a fresh batch.
 *
 * @param {{ useSendBeacon?: boolean }} [opts]
 */
export function flush(opts = {}) {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (queue.length === 0) return;
  if (destroyed) return;
  if (!getShareStats()) {
    queue = [];
    return;
  }

  const batch = queue;
  queue = [];

  const body = JSON.stringify({ events: batch });

  if (opts.useSendBeacon) {
    // sendBeacon is fire-and-forget — no retries, no error handling.
    // Used on pagehide where we can't do async work.
    try {
      navigator.sendBeacon('/api/telemetry', new Blob([body], { type: 'application/json' }));
    } catch {
      // sendBeacon can throw in some edge cases; swallow it.
    }
    return;
  }

  // Normal async POST with retry.
  void _postWithRetry(body, 0);
}

/**
 * POST a batch body to the telemetry endpoint, retrying on network
 * errors, client-side timeouts, and 5xx responses.
 *
 * @param {string} body - JSON string.
 * @param {number} attempt - Zero-based retry index.
 */
async function _postWithRetry(body, attempt) {
  if (destroyed) return;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const resp = await fetch('/api/telemetry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (resp.ok || (resp.status >= 400 && resp.status < 500)) {
      // 2xx: success. 4xx: client bug, retrying won't help.
      return;
    }

    // 5xx: server-side failure — retry if we have attempts left.
    if (attempt < config.retry_delays_ms.length) {
      setTimeout(() => _postWithRetry(body, attempt + 1), config.retry_delays_ms[attempt]);
    }
    // else: exhausted retries, batch is gone forever.
  } catch {
    // Network error or abort (timeout) — retry if we have attempts left.
    if (attempt < config.retry_delays_ms.length) {
      setTimeout(() => _postWithRetry(body, attempt + 1), config.retry_delays_ms[attempt]);
    }
  }
}

// --- Lifecycle ------------------------------------------------------------

/**
 * Install the pagehide listener so the last partial batch gets sent
 * via sendBeacon when the user navigates away or closes the tab.
 * Returns a cleanup function.
 *
 * @returns {() => void} removeListener
 */
export function installPagehideFlush() {
  function onPagehide() {
    flush({ useSendBeacon: true });
  }
  window.addEventListener('pagehide', onPagehide);
  return () => window.removeEventListener('pagehide', onPagehide);
}

/**
 * Tear down the telemetry module. Flushes the remaining queue via
 * sendBeacon, stops the timer, and prevents any further event
 * recording.
 */
export function destroy() {
  if (destroyed) return;
  destroyed = true;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  // Best-effort final flush.
  if (queue.length > 0 && getShareStats()) {
    const body = JSON.stringify({ events: queue });
    try {
      navigator.sendBeacon('/api/telemetry', new Blob([body], { type: 'application/json' }));
    } catch {
      // swallow
    }
  }
  queue = [];
}

// --- Test helpers ---------------------------------------------------------

/**
 * Reset all module state back to defaults. Test-only — allows each
 * test to start with a clean slate without module-level side effects
 * bleeding across test boundaries.
 */
export function __resetForTests() {
  destroyed = false;
  configFetched = false;
  queue = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  config = {
    batch_size: DEFAULT_BATCH_SIZE,
    flush_interval_ms: DEFAULT_FLUSH_INTERVAL_MS,
    retry_delays_ms: [...DEFAULT_RETRY_DELAYS_MS],
  };
}

/** Expose internals for white-box testing. */
export function __getQueue() {
  return queue;
}

export function __getConfig() {
  return config;
}

export function __isDestroyed() {
  return destroyed;
}
